import { Stack, Typography } from "@mui/material";
import * as coreLocales from "@mui/material/locale";
import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles";
import * as gridLocales from "@mui/x-data-grid/locales";
import * as pickersLocales from "@mui/x-date-pickers/locales";
import { useSnackbar } from "notistack";
import { Suspense, useEffect, useMemo, useState } from "react";
import { Outlet, matchPath, useLocation, useNavigate } from "react-router-dom";
import {
  fetchGetTenantInfoList,
  fetchTenantSettings,
  getRelatedMenuResourceList,
  getUserSettings,
} from "../api/admin";
import Loading from "../components/Loading";
import NestedMenu from "../components/SideMenu";
import axios from "axios";
import {
  LANGUAGEMAP,
  ROLE_TYPE_ENUM,
  pathAuthMapping,
  pathAuthWhiteList,
} from "../constants";
import {
  createPermissionSet,
  hasRoutePermission,
  parseModulePermissions,
  resolveFallbackNotificationPath,
} from "../constants/permissions";
import { useCookieData } from "../hook/useCookieData";
import { useConfigStore } from "../store/config";
import menuicon from "../assets/images/menu-icon.svg";
import { useUrfReceiveEvents } from "../hook/urfEvents";
import { useAuthStore } from "../store/auth";
import { tenantInfo } from "../api/tenant";

type SupportedLocales = keyof typeof coreLocales;
type GridLocales = keyof typeof gridLocales;
type PickersLocales = keyof typeof pickersLocales;
const Homepage = (props: any) => {
  const [auth, setAuth] = useState(false);
  const [routeAllowed, setRouteAllowed] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const linkTo = useNavigate();
  const [language] = useConfigStore((state) => [state.language]);
  const updateUserSettings = useConfigStore((state) => state.updateUserSettings);
  const updateTenantSettings = useConfigStore(
    (state) => state.updateTenantSettings
  );
  const [syncPermissions, updateIsFreyrAdmin, updateColumnHide] = useAuthStore((state) => [
    state.syncPermissions, state.updateIsFreyrAdmin, state.updateColumnHide,
  ]);
  const theme = useTheme();
  useUrfReceiveEvents();

  const { userInfo: cookieUserInfo, userId, tenantId, resolvedTenantId } = useCookieData();
  const themeWithLocale = useMemo(() => {
    const locale: SupportedLocales = LANGUAGEMAP.mui[language];
    const gridLocale: GridLocales = LANGUAGEMAP.mui[language];
    const pickersLocale: PickersLocales = LANGUAGEMAP.mui[language];
    return createTheme(
      {
        palette: {},
      },
      coreLocales[locale], //core translations
      gridLocales[gridLocale], // x-data-grid translations
      pickersLocales[pickersLocale] // x-date-pickers translations
    );
  }, [language, theme]);

  const whetherIsFreyrTenant = (userInfo: any) => {
    if (userInfo.tenantId === "0") {
      return true;
    } else if (userInfo.tenantId === "-3") {
      if (userInfo.ownFreyrTenant) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (userId) {
      setLoading(true);
      axios
        .all([
          getRelatedMenuResourceList({
            userId: userId,
            resourceType: "",
            app: "",
            module: "",
            subModule: "",
            parentId: null,
          }),
          tenantInfo(resolvedTenantId || tenantId)
        ])
        .then((data: any) => {
          const resourceData: string[] = data[0].data ?? [];
          const modulePermissions = parseModulePermissions(resourceData);
          syncPermissions(modulePermissions);
          const permissionSet = createPermissionSet(resourceData);
          let tenantInfoData = data[1].data;
          console.log(tenantInfoData.name)
          const specialTenants = process.env.REACT_APP_SPECIAL_TENANTS?.split(',') || [];
          if (specialTenants.includes(tenantInfoData.name)){
            updateColumnHide(true)
          }
          const userInfo = JSON.parse(decodeURIComponent(atob(cookieUserInfo)));
          const isFreyrTenant = whetherIsFreyrTenant(userInfo);
          const isFreyrAdmin =
            isFreyrTenant &&
            userInfo.roleTypeList.includes(ROLE_TYPE_ENUM.SUPER_ADMIN);
          updateIsFreyrAdmin(isFreyrAdmin);
          const patternPath = location.pathname
            .split("/")
            .slice(0, 4)
            .join("/");
          const hasRouteAccess = hasRoutePermission(
            permissionSet,
            patternPath,
            pathAuthMapping,
            pathAuthWhiteList,
            (pattern, pathname) => Boolean(matchPath(pattern, pathname))
          );
          setAuth(true);
          if (hasRouteAccess) {
            setRouteAllowed(true);
            return;
          }
          // Stay under /notification so Layout does not remount and re-fetch resources.
          const fallbackPath = resolveFallbackNotificationPath(permissionSet);
          if (fallbackPath && fallbackPath !== patternPath) {
            setRouteAllowed(true);
            linkTo(fallbackPath, { replace: true });
            return;
          }
          setRouteAllowed(false);
        })
        .catch((err: any) => {
          setAuth(false);
          setRouteAllowed(false);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setAuth(false);
      setRouteAllowed(false);
      setLoading(false);
    }
  }, []);

  // Fetch user + tenant regional settings independently of the page-load gate.
  // Must NOT block routing: feeds Scheduler timezone and list/datetime formatters.
  useEffect(() => {
    if (!userId) return;
    getUserSettings()
      .then((res: any) => {
        updateUserSettings(res?.data ?? null);
      })
      .catch(() => {
        updateUserSettings(null);
      });
    fetchTenantSettings()
      .then((res: any) => {
        updateTenantSettings(res?.data ?? null);
      })
      .catch(() => {
        updateTenantSettings(null);
      });
  }, [userId, updateUserSettings, updateTenantSettings]);

  const [isMenuShow, setIsMenuShow] = useState<boolean>(true);

  return (
    <>
      {auth && (
        <>
          <ThemeProvider theme={themeWithLocale}>
            <div className="layout">
              <Stack
                direction="row"
                justifyContent="end"
                className={isMenuShow ? "menu-icon" : "menuFlip-icon"}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    color: "Highlight",
                  },
                }}
                mr={2}
                onClick={() => setIsMenuShow(!isMenuShow)}
              >
                <Stack sx={{ width: "max-content" }} direction={"row"}>
                  <img src={menuicon} alt="" width="20" height="" />
                  <Typography fontSize={14} mr={0} ml={0.5}>
                    Menu
                  </Typography>
                </Stack>
              </Stack>
              <Stack flexDirection="row" sx={{ flex: 1, overflowY: "auto" }}>
                <section className="layout-right">
                  <div
                    className="page-wrapper"
                    style={{ position: "relative" }}
                  >
                    <div className="customer-page">
                      <Suspense fallback={<Loading open={true} />}>
                        {routeAllowed ? (
                          <Outlet />
                        ) : (
                          <Typography sx={{ p: 3 }} color="text.secondary">
                            You do not have permission to access this module.
                          </Typography>
                        )}
                      </Suspense>
                    </div>
                  </div>
                </section>
                <aside
                  className="layout-left"
                  style={{ display: isMenuShow ? "block" : "none" }}
                >
                  <NestedMenu></NestedMenu>
                </aside>
              </Stack>
            </div>
          </ThemeProvider>
        </>
      )}
      <Loading open={loading} />
    </>
  );
};

export default Homepage;
